"use client";

/**
 * ActionResolverModal — top-level Hub modal for the cross-entity resolver.
 *
 * Mounted once at the entity-page level. Reads source data from the global
 * modal stack (set when `executeDetailAction` intercepts a manifest-bound
 * action and calls `openModal(MODAL_IDS.actions.resolver, { actionId, sourceEntity, sourceId })`).
 *
 * Side-effect import registers manifests + handlers before resolution starts.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  MODAL_IDS,
  ResponsiveModal,
  useGlobalModalStack,
} from "@west-tennessee-consulting/ui";

import { getActionManifest } from "@/lib/actions/manifests";
import "@/lib/actions/manifests"; // side-effect: register manifests + handlers
import type { ResolverEntityKey } from "@/lib/actions/relationships/types";

import { ResolverProvider } from "./ResolverContext";
import { ResolutionPath } from "./ResolutionPath";
import { ConfirmStep } from "./steps/ConfirmStep";
import { FillMissingStep } from "./steps/FillMissingStep";
import { PickCandidateStep } from "./steps/PickCandidateStep";
import { RunStep } from "./steps/RunStep";
import { useResolverStep } from "./useResolverStep";

export const ACTION_RESOLVER_MODAL_ID = MODAL_IDS.actions.resolver;

interface ResolverModalData {
  actionId: string;
  sourceEntity: ResolverEntityKey;
  sourceId: string;
}

function isResolverModalData(v: unknown): v is ResolverModalData {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.actionId === "string" &&
    typeof o.sourceEntity === "string" &&
    typeof o.sourceId === "string"
  );
}

function useResolverModalSession(): {
  open: boolean;
  session: ResolverModalData | null;
  close: () => void;
} {
  const { isModalOpen, closeModal, getModalData, modalDataRevision } =
    useGlobalModalStack();
  const open = isModalOpen(ACTION_RESOLVER_MODAL_ID);
  const close = useCallback(
    () => closeModal(ACTION_RESOLVER_MODAL_ID),
    [closeModal],
  );

  const session = useMemo(() => {
    if (!open) {
      return null;
    }
    const raw = getModalData(ACTION_RESOLVER_MODAL_ID);
    return isResolverModalData(raw) ? raw : null;
  }, [open, modalDataRevision, getModalData]);

  return { open, session, close };
}

export function ActionResolverModal() {
  const { open, session, close } = useResolverModalSession();

  if (!open || !session) return null;

  const manifest = getActionManifest(session.actionId);
  const title = manifest?.label ?? "Run action";
  const sessionKey = `${session.actionId}:${session.sourceEntity}:${session.sourceId}`;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={title}
      globalModalId={ACTION_RESOLVER_MODAL_ID}
    >
      <ResolverProvider
        key={sessionKey}
        actionId={session.actionId}
        sourceEntity={session.sourceEntity}
        sourceId={session.sourceId}
        onClose={close}
      >
        <ResolverBody onClose={close} />
      </ResolverProvider>
    </ResponsiveModal>
  );
}

function ResolverBody({ onClose }: { onClose: () => void }) {
  const state = useResolverStep();
  const [doneMessage, setDoneMessage] = useState<{
    ok: boolean;
    message?: string;
  } | null>(null);

  useEffect(() => {
    if (doneMessage?.ok) {
      const t = setTimeout(onClose, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [doneMessage, onClose]);

  if (doneMessage) {
    return (
      <div className="space-y-2 text-sm">
        <div className={doneMessage.ok ? "text-emerald-600" : "text-destructive"}>
          {doneMessage.ok ? "Done." : "Failed."}
        </div>
        {doneMessage.message && (
          <div className="text-muted-foreground">{doneMessage.message}</div>
        )}
      </div>
    );
  }

  if (state.status === "loading") {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (state.status === "error") {
    return (
      <div className="text-sm text-destructive" role="alert">
        {state.message}
      </div>
    );
  }

  const { step } = state;
  return (
    <div className="space-y-3">
      <ResolutionPath walk={step.kind === "pick" ? step.walk : null} />
      {step.kind === "pick" && (
        <PickCandidateStep
          stepIndex={step.stepIndex}
          candidates={step.candidates}
          allowMulti={step.allowMulti}
          bulkMax={step.bulkMax}
        />
      )}
      {step.kind === "fill" && (
        <FillMissingStep
          missingFields={step.missingFields}
          extraInputs={step.extraInputs}
        />
      )}
      {step.kind === "confirm" && <ConfirmStep summary={step.summary} />}
      {step.kind === "run" && (
        <RunStep
          collected={state.runCollected}
          prefetchedTargets={state.prefetchedTargets}
          onDone={(ok, message) => setDoneMessage({ ok, message })}
        />
      )}
    </div>
  );
}
